import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, ArrowRight, XCircle, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useLanguage } from "@/i18n/LanguageContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuccessPage = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= maxAttempts) {
      setStatus("timeout");
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === "paid") {
        setStatus("success");
        setPaymentData(data);
      } else if (data.status === "expired") {
        setStatus("expired");
      } else {
        setAttempts((prev) => prev + 1);
      }
    } catch (error) {
      console.error("Error checking status:", error);
      setAttempts((prev) => prev + 1);
    }
  }, [sessionId, attempts, maxAttempts]);

  useEffect(() => {
    if (sessionId && status === "loading") {
      pollPaymentStatus();
    } else if (!sessionId) {
      setStatus("error");
    }
  }, [sessionId, status, pollPaymentStatus]);

  useEffect(() => {
    if (attempts > 0 && attempts < maxAttempts && status === "loading") {
      const timer = setTimeout(pollPaymentStatus, 2000);
      return () => clearTimeout(timer);
    } else if (attempts >= maxAttempts && status === "loading") {
      setStatus("error");
    }
  }, [attempts, maxAttempts, status, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-stone-50" data-testid="success-page">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-xl mx-auto text-center">
          {status === "loading" && (
            <div className="animate-fade-in" data-testid="payment-loading">
              <Loader2 className="w-16 h-16 animate-spin text-[#2c4c3b] mx-auto mb-6" />
              <h1 className="font-serif text-3xl text-stone-900 mb-4">
                {t("success.verifying")}
              </h1>
              <p className="text-stone-600">
                {t("success.wait")}
              </p>
            </div>
          )}

          {status === "success" && (
            <div className="animate-fade-in-up" data-testid="payment-success">
              <div className="w-24 h-24 rounded-full bg-[#2c4c3b] flex items-center justify-center mx-auto mb-8">
                <Check className="w-12 h-12 text-stone-100" />
              </div>
              <h1 className="font-serif text-4xl text-stone-900 mb-4">
                {t("success.completed")}
              </h1>
              <p className="text-stone-600 mb-8 leading-relaxed">
                {t("success.thanks")}
              </p>

              {paymentData && (
                <div className="bg-[#e8ece6] rounded-2xl p-6 mb-8 text-left">
                  <h3 className="font-medium text-stone-900 mb-3">{t("success.summary")}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-stone-600">
                      <span className="text-stone-500">{t("success.amount")}</span>{" "}
                      <span className="font-medium text-[#2c4c3b]">
                        {paymentData.currency === "usd" ? "$" : "€"}{(paymentData.amount_total / 100).toFixed(2)}
                      </span>
                    </p>
                    <p className="text-stone-600">
                      <span className="text-stone-500">{t("success.status")}</span>{" "}
                      <span className="font-medium text-[#2c4c3b]">{t("success.paid")}</span>
                    </p>
                  </div>
                </div>
              )}

              <div className="bg-white border border-stone-100 rounded-2xl p-6 mb-8 flex items-start gap-4 text-left">
                <div className="w-10 h-10 rounded-xl bg-[#e8ece6] flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="w-5 h-5 text-[#2c4c3b]" />
                </div>
                <p className="text-sm text-stone-600 leading-relaxed">
                  {t("success.questionnaireHint")}
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/area-paziente">
                  <Button className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-8 py-6 group" data-testid="go-patient-area-btn">
                    {t("success.patientAreaCta")}
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="rounded-full px-8 py-6 border-stone-300 text-stone-600">
                    {t("success.backHome")}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {(status === "error" || status === "timeout" || status === "expired") && (
            <div className="animate-fade-in" data-testid="payment-error">
              <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-8">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="font-serif text-3xl text-stone-900 mb-4">
                {status === "expired" ? t("success.expired") : t("success.failed")}
              </h1>
              <p className="text-stone-600 mb-8">
                {status === "expired" ? t("success.expiredText") : t("success.failedText")}
              </p>
              <Link to="/pricing">
                <Button className="bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full px-8 py-6">
                  {t("success.backToPlans")}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SuccessPage;
